import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@lawline/ui/components/card";

import { authClient } from "@platform/auth/client";
import {
	sessionMutationOptions,
	useSessionTransition,
} from "@platform/auth/session-transition.context";

export function AcceptInvitationPage() {
	const t = useTranslations("auth.acceptInvitation");
	const { id } = useSearch({ from: "/accept-invitation" });
	const changeSession = useSessionTransition();
	const invitation = useQuery({
		queryKey: ["invitation", id],
		enabled: Boolean(id),
		retry: false,
		queryFn: async () => {
			const { data, error } = await authClient.organization.getInvitation({
				query: { id },
				fetchOptions: sessionMutationOptions,
			});
			if (error || !data) throw new Error(error?.message ?? t("expiredError"));
			return data;
		},
	});
	const error = !id ? t("invalidLinkError") : invitation.error ? t("loadError") : null;
	const invite = invitation.data;

	async function accept() {
		if (!invite) return;
		await changeSession(async () => {
			const { data, error } = await authClient.organization.acceptInvitation({
				invitationId: id,
				fetchOptions: sessionMutationOptions,
			});
			if (
				error ||
				data?.invitation.id !== id ||
				data.member.organizationId !== invite.organizationId
			) {
				throw new Error("Invitation acceptance is unconfirmed");
			}
			return "reload";
		});
	}

	async function useDifferentAccount() {
		await changeSession(
			async () => {
				const { data, error } = await authClient.signOut({ fetchOptions: sessionMutationOptions });
				if (error || !data?.success) throw new Error("Sign out is unconfirmed");
				return "reload";
			},
			`/accept-invitation?id=${encodeURIComponent(id)}`,
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center p-6">
			<Card className="w-full max-w-sm">
				<CardHeader className="text-center">
					<CardTitle>
						{error
							? t("title")
							: invite
								? t("joinTitle", { name: invite.organizationName })
								: t("loadingTitle")}
					</CardTitle>
					<CardDescription>
						{error ?? (invite ? t("recipient", { email: invite.email }) : t("loadingMessage"))}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-2">
					{!error && invite && <Button onClick={accept}>{t("accept")}</Button>}
					{error && id && (
						<Button onClick={() => void invitation.refetch()} disabled={invitation.isFetching}>
							{t("retry")}
						</Button>
					)}
				</CardContent>
				<CardFooter className="flex flex-col gap-2">
					<Button variant="outline" onClick={useDifferentAccount}>
						{t("useDifferentAccount")}
					</Button>
					<Button variant="ghost" onClick={() => window.location.assign("/tenant")}>
						{t("dashboard")}
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
